import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import ErrorNotice from "../../errors/ErrorNotice";
import AppButton from "../../ui/buttons/AppButton";
import type { EditSession } from "./useEditSession";

interface EditSessionBarProps<Edit> {
  session: EditSession<Edit>;
  /** "Save" in the owner's words, such as "Propose" where a save goes to review. */
  saveLabel: string;
  /** Where the edits go, when the owner lets the person choose, such as a change set. */
  children?: ReactNode;
  /** A reason the edits can't be saved yet. */
  saveDisabled?: boolean;
}

/** While edits made in place wait: how many, and one Save or Discard for all of them (F96). */
export default function EditSessionBar<Edit>({ session, saveLabel, children, saveDisabled = false }: EditSessionBarProps<Edit>) {
  const { t } = useTranslation();
  const { edits, saving } = session;
  if (edits.length === 0) return null;
  const busy = saving.status === "submitting";
  return (
    <section className="app-surface app-edit-session-bar space-y-3 rounded border p-3" aria-label={t("editing.unsaved", { count: edits.length })}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="app-text min-w-0 flex-1 text-sm font-medium" aria-live="polite">
          {t("editing.unsaved", { count: edits.length })}
        </p>
        <AppButton variant="secondary" size="sm" disabled={busy} onClick={session.discard}>
          {t("editing.discard")}
        </AppButton>
        <AppButton variant="primary" size="sm" disabled={busy || saveDisabled || session.blocked} onClick={session.save}>
          {busy ? t("editing.saving") : saveLabel}
        </AppButton>
      </div>
      {session.blocked ? <p className="app-error-text text-xs">{t("editing.blocked")}</p> : null}
      {children}
      {saving.status === "failed" ? <ErrorNotice error={saving.error} /> : null}
    </section>
  );
}
